import React, { useEffect, useState } from 'react';
import type { JobInfoRoleDetail } from '../../api/jobInfoApi';
import '../../styles/interview-detail-modal.css';
import './JobRoleDetailModal.css';

interface JobRoleDetailPanelProps {
  role: JobInfoRoleDetail | null;
  isLoading: boolean;
  error: string | null;
  isBuilding?: boolean;
  onClose: () => void;
  onBuildInterview: (selectedLevel?: number) => void;
  onRelatedRoleSelect?: (roleId: string) => void;
}

const JobRoleDetailPanel: React.FC<JobRoleDetailPanelProps> = ({
  role,
  isLoading,
  error,
  isBuilding = false,
  onClose,
  onBuildInterview,
  onRelatedRoleSelect,
}) => {
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const hasLevels = (role?.responsibility_levels.length ?? 0) > 0;
  const canBuild = !hasLevels || selectedLevel != null;

  useEffect(() => {
    setSelectedLevel(null);
  }, [role?.role_id]);

  const handleLevelToggle = (levelNum: number) => {
    setSelectedLevel((prev) => (prev === levelNum ? null : levelNum));
  };

  const handleBuild = () => {
    if (!canBuild) return;
    onBuildInterview(selectedLevel ?? undefined);
  };

  const selectedLevelInfo =
    selectedLevel != null
      ? role?.responsibility_levels.find((level) => level.level === selectedLevel)
      : undefined;

  const levelPillLabel = selectedLevelInfo
    ? `Level ${selectedLevelInfo.level} — ${selectedLevelInfo.level_name}`
    : 'No levels selected';

  return (
    <div className="interview-detail-modal">
      <button type="button" className="interview-detail-modal-close" onClick={onClose}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>

      <div className="interview-detail-modal-content">
        <div className="interview-detail-modal-scroll">
          {isLoading ? (
            <div className="job-role-detail__loading">
              <span className="interview-detail-modal-loader" role="status" aria-label="Loading role" />
              <p>Loading role details...</p>
            </div>
          ) : error ? (
            <div className="job-role-detail__error">{error}</div>
          ) : role ? (
            <div className="job-role-detail-card">
              <div className="job-role-detail-card__header">
                <div className="job-role-detail-card__title-cell">
                  <span className="job-role-detail-card__label">Role</span>
                  <h3>{role.role_name}</h3>
                </div>
                <div className="job-role-detail-card__domain-cell">
                  <span className="job-role-detail-card__label">Domain</span>
                  <span className="job-role-detail-card__domain">{role.domain}</span>
                </div>
              </div>

              <div className="job-role-detail-card__body">
                <section className="job-role-detail-card__section">
                  <h4>Overview</h4>
                  <p>{role.overview}</p>
                </section>

                {role.key_activities.length > 0 && (
                  <section className="job-role-detail-card__section">
                    <h4>Key Activities</h4>
                    <ul>
                      {role.key_activities.map((activity, index) => (
                        <li key={index}>{activity}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {hasLevels && (
                  <section className="job-role-detail-card__section">
                    <div className="job-role-detail-card__levels-header">
                      <h4>Responsibility Levels</h4>
                      <p className="job-role-detail-card__levels-hint">
                        Select a level to target that responsibility in the interview
                      </p>
                    </div>
                    <div
                      className="job-role-detail-card__levels"
                      role="radiogroup"
                      aria-label="Responsibility levels"
                    >
                      {role.responsibility_levels.map((level) => {
                        const isSelected = selectedLevel === level.level;
                        const isMuted = selectedLevel != null && !isSelected;

                        return (
                          <div
                            key={level.level}
                            className={`job-role-detail-card__level${
                              isSelected ? ' job-role-detail-card__level--selected' : ''
                            }${isMuted ? ' job-role-detail-card__level--muted' : ''}`}
                          >
                            <div className="job-role-detail-card__level-top">
                              <div className="job-role-detail-card__level-header">
                                <span className="job-role-detail-card__level-badge">
                                  Level {level.level}
                                </span>
                                <span className="job-role-detail-card__level-name">{level.level_name}</span>
                              </div>
                              <div className="job-role-detail-card__level-select">
                                <button
                                  type="button"
                                  className="job-role-detail-card__level-select-btn"
                                  onClick={(e) => {
                                    handleLevelToggle(level.level);
                                    e.currentTarget.blur();
                                  }}
                                >
                                  {isSelected ? 'Deselect' : 'Select'}
                                </button>
                                <button
                                  type="button"
                                  className={`job-role-detail-card__level-radio${
                                    isSelected ? ' job-role-detail-card__level-radio--checked' : ''
                                  }`}
                                  role="radio"
                                  aria-checked={isSelected}
                                  aria-label={`${isSelected ? 'Deselect' : 'Select'} level ${level.level}: ${level.level_name}`}
                                  onClick={(e) => {
                                    handleLevelToggle(level.level);
                                    e.currentTarget.blur();
                                  }}
                                >
                                  <span className="job-role-detail-card__level-radio-mark" aria-hidden />
                                </button>
                              </div>
                            </div>
                            <p className="job-role-detail-card__level-summary">{level.summary}</p>
                            <p className="job-role-detail-card__level-description">
                              {level.role_specific_description}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

                {role.related_roles.length > 0 && (
                  <section className="job-role-detail-card__section">
                    <h4>Related Skills and Roles</h4>
                    <div className="job-role-detail-card__related">
                      {role.related_roles.map((related) => (
                        <button
                          key={related.role_id}
                          type="button"
                          className="job-role-detail-card__related-pill"
                          onClick={() => onRelatedRoleSelect?.(related.role_id)}
                        >
                          {related.role_name}
                        </button>
                      ))}
                    </div>
                  </section>
                )}

                {role.reference_url && (
                  <section className="job-role-detail-card__section">
                    <h4>Reference</h4>
                    <a
                      className="job-role-detail-card__reference"
                      href={role.reference_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {role.reference_url}
                    </a>
                  </section>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {role && !isLoading && !error && (
          <div className="interview-detail-modal-actions">
            <div className="interview-detail-modal-action-row job-role-build-action-row">
              {hasLevels && (
                <span
                  className={`job-role-build-level-pill${
                    selectedLevelInfo ? '' : ' job-role-build-level-pill--empty'
                  }`}
                  title={levelPillLabel}
                >
                  {levelPillLabel}
                </span>
              )}
              <div className="job-role-build-action-row__end">
                {isBuilding && (
                  <span
                    className="interview-detail-modal-loader"
                    role="status"
                    aria-label="Building interview"
                  />
                )}
                <button
                  type="button"
                  className="interview-detail-customise-btn"
                  onClick={handleBuild}
                  disabled={isBuilding || !canBuild}
                  aria-busy={isBuilding}
                  title={!canBuild && hasLevels ? 'Select a responsibility level first' : undefined}
                >
                  Build interview
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobRoleDetailPanel;
